/**
 * P-007 c2/c9/c20 — encrypted offline store (IndexedDB).
 *
 * Two object stores in a single IndexedDB database:
 *   - `records`: locally-cached health data (for offline reads / SW-timer alerts), encrypted at rest
 *                with the device-bound key (crypto.js).
 *   - `queue`:   pending offline edits awaiting sync, each tagged with a health-criticality priority
 *                (priority.js) and a monotonic seq for stable ordering.
 * Every value is sealed with encryptRecord() before it touches disk, so no plaintext PHI is stored.
 */
import { encryptRecord, decryptRecord } from './crypto';
import { priorityFor } from './priority';

const DB_NAME = 'healist-offline';
const DB_VERSION = 1;
const RECORDS = 'records';
const QUEUE = 'queue';

function _open() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(RECORDS)) db.createObjectStore(RECORDS, { keyPath: 'key' });
      if (!db.objectStoreNames.contains(QUEUE)) db.createObjectStore(QUEUE, { keyPath: 'seq', autoIncrement: true });
    };
    req.onsuccess = () => {
      const db = req.result;
      // D11 (2026-10-21): sign-out / sign-in deletes this database (utils/clearUserData.js). A connection that does
      // not close on `versionchange` blocks the delete, and every later open() would queue behind it forever.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    req.onerror = () => reject(req.error);
  });
}

function _tx(db, store, mode, fn) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const r = fn(tx.objectStore(store));
    tx.oncomplete = () => resolve(r && 'result' in r ? r.result : undefined);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

// ---- cached records (offline reads) ----
export async function putRecord(type, id, value) {
  const db = await _open();
  const env = await encryptRecord({ type, id, value });
  return _tx(db, RECORDS, 'readwrite', (s) => s.put({ key: `${type}:${id}`, type, env, cachedAt: Date.now() }));
}

export async function getRecord(type, id) {
  const db = await _open();
  const row = await _tx(db, RECORDS, 'readonly', (s) => s.get(`${type}:${id}`));
  if (!row) return null;
  return (await decryptRecord(row.env)).value;
}

export async function getRecordsByType(type) {
  const db = await _open();
  const all = await _tx(db, RECORDS, 'readonly', (s) => s.getAll());
  const out = [];
  for (const row of all.filter((r) => r.type === type)) out.push((await decryptRecord(row.env)).value);
  return out;
}

// ---- sync queue (pending offline edits) ----
export async function enqueue(edit) {
  const db = await _open();
  const priority = edit.priority ?? priorityFor(edit.type);
  const env = await encryptRecord(edit);
  return _tx(db, QUEUE, 'readwrite', (s) => s.add({ type: edit.type, priority, env, enqueuedAt: Date.now() }));
}

export async function getQueue() {
  const db = await _open();
  const rows = await _tx(db, QUEUE, 'readonly', (s) => s.getAll());
  const out = [];
  for (const row of rows) {
    const edit = await decryptRecord(row.env);
    out.push({ ...edit, seq: row.seq, priority: row.priority });
  }
  return out;
}

export async function dequeue(seq) {
  const db = await _open();
  return _tx(db, QUEUE, 'readwrite', (s) => s.delete(seq));
}

export async function queueSize() {
  const db = await _open();
  return _tx(db, QUEUE, 'readonly', (s) => s.count());
}
